import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CertificatePreview } from "@/components/consultant/CertificatePreview";
import { AgentChatDialog } from "@/components/consultant/AgentChatDialog";
import {
  ArrowLeft, CheckCircle2, Circle, Users, Calendar, FileText, 
  MessageSquare, BarChart3, Mic, Sparkles, Mail, Settings,
  ChevronRight, BookOpen, Target, Zap, Shield, Clock,
  CreditCard, Bell, UserPlus, Layout, PenTool, Send,
  Bot, Award, Megaphone
} from "lucide-react";

const ONBOARDING_STEPS = [
  {
    id: 1,
    title: "Dashboard Overview",
    description: "Get familiar with your command center",
    icon: Layout,
    color: "bg-purple-500",
    content: [
      {
        title: "Access Your Dashboard",
        details: "Navigate to /consultant after logging in. This is your home base for managing all clients, content, and business operations."
      },
      {
        title: "Dashboard Sections",
        details: "Your dashboard includes: Client Management, Bulletin Board, Community, Newsletters, Events, and Analytics tabs."
      },
      {
        title: "Quick Stats",
        details: "The header shows key metrics: Total Clients, Active Projects, Upcoming Events, and Pending Tasks at a glance."
      }
    ]
  },
  {
    id: 2,
    title: "Client Management",
    description: "Invite and manage your clients",
    icon: Users,
    color: "bg-blue-500",
    content: [
      {
        title: "Invite New Clients",
        details: "Click 'Invite User' button → Choose 'Client' role → Select their tier (Basic, Growth, Scale, or Podcast Academy) → Send invite via email or create their account directly."
      },
      {
        title: "Client Tiers Explained",
        details: "Basic ($250/mo): Essential consulting. Growth ($500/mo): Expanded support. Scale ($1000/mo): Full-service partnership. Podcast Academy ($500 one-time): 12-week program."
      },
      {
        title: "Manage Client Details",
        details: "Click any client card to view their profile, edit contact info, reset passwords, change tiers, or view their progress through programs."
      },
      {
        title: "Client Intake Forms",
        details: "Use 'Send Intake Form First' option to gather business info before granting dashboard access. Review and approve submissions from the client detail page."
      }
    ]
  },
  {
    id: 3,
    title: "Community Building",
    description: "Grow and engage your community",
    icon: MessageSquare,
    color: "bg-green-500",
    content: [
      {
        title: "Community Members",
        details: "Invite users as 'Community Members' for free access to community features. They can upgrade to paid client tiers later."
      },
      {
        title: "Bulletin Board",
        details: "Post updates, tips, and announcements. Use AI generation to create content by category. Schedule posts for future publication."
      },
      {
        title: "Auto-Bulletin Feature",
        details: "Enable automatic daily posts at 8 AM. Set your preferred category and let AI generate relevant content automatically."
      },
      {
        title: "Moderate Comments",
        details: "Review pending comments in the 'Community View' tab. Approve or reject comments to maintain quality discussions."
      }
    ]
  },
  {
    id: 4,
    title: "Events & Workshops",
    description: "Create and manage events",
    icon: Calendar,
    color: "bg-amber-500",
    content: [
      {
        title: "Create Events",
        details: "Go to Events tab → Click 'Create Event' → Fill in title, description, date/time, location, and optional payment/meeting links."
      },
      {
        title: "Event Types",
        details: "Free workshops (community building), Paid events (use payment link for Stripe checkout), Online meetings (add Zoom/meeting link)."
      },
      {
        title: "Manage Registrations",
        details: "View registered attendees, send reminder emails, or remove attendees as needed."
      },
      {
        title: "Public Event Sharing",
        details: "Share event URLs publicly. Non-members will be prompted to join the community before registering."
      }
    ]
  },
  {
    id: 5,
    title: "AI-Powered Tools",
    description: "Leverage AI for client success",
    icon: Sparkles,
    color: "bg-pink-500",
    content: [
      {
        title: "Scaling Dashboard Tools",
        details: "Access via /scaling: Business Health Snapshot, SOP Generator, Business Plan, Time Budget, Implementation Plan, and more."
      },
      {
        title: "AI Generators Available",
        details: "Ideal Client Profile, StoryBrand Framework, Brand Guidelines, Org Map, KPI Scorecard, Marketing Strategy, Tech Stack Recommendations."
      },
      {
        title: "Preview as Client",
        details: "Use the 'Preview as Client' button to see exactly how AI outputs will appear to your clients before sending."
      },
      {
        title: "Export & Email Outputs",
        details: "Download generated content as text files or email directly to clients with one click."
      }
    ]
  },
  {
    id: 6,
    title: "Podcast Academy Management",
    description: "Coach your podcasting students",
    icon: Mic,
    color: "bg-red-500",
    content: [
      {
        title: "Access Academy Admin",
        details: "Navigate to /consultant/podcast-academy to manage all enrolled students."
      },
      {
        title: "Track Student Progress",
        details: "View each student's current week, completed lessons, and overall progress through the 12-week curriculum."
      },
      {
        title: "Provide Feedback",
        details: "Click 'Feedback' on any student card → Select week → Add rating → Write personalized coaching notes. Students receive email notifications."
      },
      {
        title: "Mark Progress Complete",
        details: "Use the Progress Tracker tab to manually advance students or mark weeks as completed."
      }
    ]
  },
  {
    id: 7,
    title: "Payments & Analytics",
    description: "Track revenue and payments",
    icon: CreditCard,
    color: "bg-emerald-500",
    content: [
      {
        title: "Payment Dashboard",
        details: "Access via /consultant/payments to view all payment analytics and transaction history."
      },
      {
        title: "Stripe Analytics",
        details: "View total revenue, monthly trends, product performance, conversion rates, and top customers."
      },
      {
        title: "Manual Payments",
        details: "Record offline payments (cash, check, etc.) for clients not using Stripe."
      },
      {
        title: "Transaction History",
        details: "See all successful payments with discounts applied, credits used, and receipt generation."
      }
    ]
  },
  {
    id: 8,
    title: "Newsletters & Email",
    description: "Communicate with your audience",
    icon: Mail,
    color: "bg-indigo-500",
    content: [
      {
        title: "AI Newsletter Generation",
        details: "Generate professional newsletters with AI. Customize topic, tone, and target audience."
      },
      {
        title: "Send Newsletters",
        details: "Preview before sending, then distribute to your entire community or specific segments."
      },
      {
        title: "Auto-Newsletter",
        details: "Enable weekly automatic newsletters. Set preferred day and topic themes."
      },
      {
        title: "Email History",
        details: "Track all sent newsletters with open rates and engagement metrics."
      }
    ]
  },
  {
    id: 9,
    title: "AI Agents & Automation",
    description: "Your autonomous team of 10 AI agents",
    icon: Bot,
    color: "bg-violet-500",
    content: [
      {
        title: "Meet Your Agents",
        details: "You have 10 AI agents: Jasmine (Onboarding), Marcus (Growth), Tanya (Community), Derek (Networking), Simone (Opportunities), Kev (Gamification), Whitney (Mastermind Pods), Andre (Retention), Carmen (Weekly Digest), and Miles (Community Engagement)."
      },
      {
        title: "Miles — Community Engagement",
        details: "Miles posts to the discussion board every few hours, welcomes new members with DMs, and keeps conversations flowing. Manage Miles from the sidebar under Community > Miles Agent."
      },
      {
        title: "Chat with Agents",
        details: "Need help with a task? Open 'Talk to an Agent' from this page or the Help Center. Each agent specializes in different areas — Jasmine for onboarding, Marcus for growth strategy, etc."
      },
      {
        title: "Agent Management",
        details: "Monitor agent activity and stats from the sidebar panels. Toggle agents on/off, trigger actions manually, and review their performance."
      }
    ]
  },
  {
    id: 10,
    title: "Mass Outreach & Growth",
    description: "Scale your community with outreach campaigns",
    icon: Megaphone,
    color: "bg-rose-500",
    content: [
      {
        title: "Mass Outreach Campaign",
        details: "Navigate to Community > Mass Outreach in the sidebar to manage subscriber campaigns. Import contacts from your CSV, send in waves of 25, and auto-send every 4 hours."
      },
      {
        title: "Import Subscribers",
        details: "Click 'Import Subscribers' to load your contact list. Existing platform members are automatically excluded to avoid duplicates."
      },
      {
        title: "Campaign Controls",
        details: "Start auto-waves, pause/resume campaigns, send test batches, and monitor progress with real-time stats — all from the Outreach Panel."
      },
      {
        title: "View Contact Status",
        details: "Browse imported contacts with pagination, filter by status (pending/sent/failed), and track which wave each contact was sent in."
      }
    ]
  },
  {
    id: 11,
    title: "Completion Certificates",
    description: "Recognize client achievements",
    icon: Award,
    color: "bg-amber-500",
    content: [
      {
        title: "Program Certificates",
        details: "Clients who complete R.A.P.I.D., Podcast Academy, or Strategic Scaling Program receive professional PDF certificates recognizing their achievement."
      },
      {
        title: "Unlock & Download",
        details: "As a consultant, you can unlock certificates for clients who complete programs. Clients can then download their certificates from their dashboard."
      },
      {
        title: "Preview Certificates",
        details: "Use the 'Preview Certificates' button on this page to see sample certificates for all three programs before sharing them with clients."
      }
    ]
  }
];

const QUICK_ACTIONS = [
  { title: "Invite First Client", path: "/consultant", action: "addClient", icon: UserPlus },
  { title: "Create Bulletin Post", path: "/consultant", action: "bulletin", icon: PenTool },
  { title: "Schedule Event", path: "/consultant", action: "events", icon: Calendar },
  { title: "View AI Tools", path: "/scaling", icon: Sparkles },
  { title: "Podcast Academy", path: "/consultant/podcast-academy", icon: Mic },
  { title: "Payment Analytics", path: "/consultant/payments", icon: BarChart3 },
  { title: "Miles Agent", path: "/consultant", action: "milesAgent", icon: MessageSquare },
  { title: "Mass Outreach", path: "/consultant", action: "outreach", icon: Megaphone }
];

export const ConsultantOnboarding = () => {
  const navigate = useNavigate();
  const [completedSteps, setCompletedSteps] = useState([]);
  const [showCertificates, setShowCertificates] = useState(false);
  const [showAgentChat, setShowAgentChat] = useState(false);

  const toggleStep = (stepId) => {
    setCompletedSteps(prev => 
      prev.includes(stepId) 
        ? prev.filter(id => id !== stepId)
        : [...prev, stepId]
    );
  };

  const progressPercent = Math.round((completedSteps.length / ONBOARDING_STEPS.length) * 100);

  return (
    <div className="min-h-screen bg-neutral-50 overflow-x-hidden" data-testid="consultant-onboarding">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-800 to-emerald-600 text-white">
        <div className="w-full max-w-6xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 sm:mb-6">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/consultant")}
              className="text-white/80 hover:text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Back to Dashboard</span>
              <span className="sm:hidden">Back</span>
            </Button>
            <Badge className="bg-white/20 text-white text-xs sm:text-sm">
              {completedSteps.length} of {ONBOARDING_STEPS.length} completed
            </Badge>
          </div>
          
          <div className="text-center max-w-2xl mx-auto px-2">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-3">
              Welcome to Your Consultant Dashboard
            </h1>
            <p className="text-emerald-100 text-sm sm:text-base md:text-lg mb-4 sm:mb-6">
              Follow this step-by-step guide to master your dashboard and start helping clients succeed.
            </p>
            
            <div className="bg-white/10 rounded-lg p-3 sm:p-4">
              <div className="flex items-center justify-between text-xs sm:text-sm mb-2">
                <span>Onboarding Progress</span>
                <span className="font-semibold">{progressPercent}%</span>
              </div>
              <Progress value={progressPercent} className="h-2 bg-white/20" />
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-6xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8">
        {/* Quick Actions */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-base sm:text-lg font-semibold text-neutral-900 mb-3 sm:mb-4">Quick Actions</h2>
          <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3">
            {QUICK_ACTIONS.map((action, idx) => (
              <Button
                key={idx}
                variant="outline"
                className="h-auto py-3 sm:py-4 flex flex-col items-center gap-1 sm:gap-2 hover:bg-emerald-50 hover:border-emerald-300 px-1 sm:px-2"
                onClick={() => navigate(action.path, action.action ? { state: { quickAction: action.action } } : undefined)}
              >
                <action.icon className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                <span className="text-[10px] sm:text-xs text-center leading-tight">{action.title}</span>
              </Button>
            ))}
          </div>
        </div>

        {/* Onboarding Steps */}
        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-base sm:text-lg font-semibold text-neutral-900">Step-by-Step Guide</h2>
          
          {ONBOARDING_STEPS.map((step) => {
            const isCompleted = completedSteps.includes(step.id);
            const StepIcon = step.icon;
            
            return (
              <Card 
                key={step.id} 
                className={`border-2 transition-all overflow-hidden ${
                  isCompleted ? 'border-emerald-300 bg-emerald-50/50' : 'border-neutral-200'
                }`}
              >
                <Accordion type="single" collapsible>
                  <AccordionItem value={`step-${step.id}`} className="border-none">
                    <AccordionTrigger className="px-3 sm:px-6 py-3 sm:py-4 hover:no-underline">
                      <div className="flex items-center gap-2 sm:gap-4 w-full">
                        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl ${step.color} flex items-center justify-center text-white flex-shrink-0`}>
                          <StepIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                            <span className="text-xs sm:text-sm text-neutral-500">Step {step.id}</span>
                            {isCompleted && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-[10px] sm:text-xs">Done</Badge>
                            )}
                          </div>
                          <h3 className="font-semibold text-neutral-900 text-sm sm:text-base truncate">{step.title}</h3>
                          <p className="text-xs sm:text-sm text-neutral-500 hidden sm:block">{step.description}</p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 sm:px-6 pb-4 sm:pb-6">
                      <div className="sm:ml-14 md:ml-16 space-y-3 sm:space-y-4">
                        {step.content.map((item, idx) => (
                          <div key={idx} className="flex gap-2 sm:gap-3">
                            <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-[10px] sm:text-xs font-semibold text-emerald-700">{idx + 1}</span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <h4 className="font-medium text-neutral-900 text-sm sm:text-base">{item.title}</h4>
                              <p className="text-xs sm:text-sm text-neutral-600 mt-1 break-words">{item.details}</p>
                            </div>
                          </div>
                        ))}
                        
                        <Button
                          variant={isCompleted ? "outline" : "default"}
                          size="sm"
                          onClick={() => toggleStep(step.id)}
                          className={`mt-2 ${isCompleted ? "border-emerald-300 text-emerald-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
                        >
                          {isCompleted ? (
                            <>
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Completed
                            </>
                          ) : (
                            <>
                              <Circle className="w-4 h-4 mr-2" />
                              Mark as Complete
                            </>
                          )}
                        </Button>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </Card>
            );
          })}
        </div>

        {/* Training & Agent Interaction Section */}
        <Card className="mt-6 sm:mt-8 border-violet-200 bg-violet-50/50">
          <CardHeader className="px-4 sm:px-6 py-4 sm:py-6">
            <CardTitle className="flex items-center gap-2 text-violet-800 text-base sm:text-lg">
              <Bot className="w-4 h-4 sm:w-5 sm:h-5" />
              Training & Agent Assistance
            </CardTitle>
            <CardDescription className="text-violet-600 text-xs sm:text-sm">
              Interact with your autonomous agents for help, or preview program certificates.
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6 pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                onClick={() => setShowAgentChat(true)}
                className="p-4 rounded-xl border-2 border-violet-200 hover:border-violet-400 bg-white hover:bg-violet-50 transition-all text-left"
                data-testid="open-agent-chat-btn"
              >
                <Sparkles className="w-6 h-6 text-violet-600 mb-2" />
                <h4 className="font-semibold text-sm text-neutral-900">Talk to an Agent</h4>
                <p className="text-xs text-neutral-500 mt-1">Get help from Jasmine, Marcus, Miles, and more. Ask about platform features or business strategy.</p>
              </button>
              <button
                onClick={() => setShowCertificates(true)}
                className="p-4 rounded-xl border-2 border-amber-200 hover:border-amber-400 bg-white hover:bg-amber-50 transition-all text-left"
                data-testid="open-certificates-btn"
              >
                <Award className="w-6 h-6 text-amber-600 mb-2" />
                <h4 className="font-semibold text-sm text-neutral-900">Preview Certificates</h4>
                <p className="text-xs text-neutral-500 mt-1">See sample completion certificates for R.A.P.I.D., Podcast Academy, and Strategic Scaling.</p>
              </button>
              <button
                onClick={() => navigate("/help")}
                className="p-4 rounded-xl border-2 border-emerald-200 hover:border-emerald-400 bg-white hover:bg-emerald-50 transition-all text-left"
              >
                <BookOpen className="w-6 h-6 text-emerald-600 mb-2" />
                <h4 className="font-semibold text-sm text-neutral-900">Help Center</h4>
                <p className="text-xs text-neutral-500 mt-1">Browse FAQs, documentation, and troubleshooting guides for the platform.</p>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Tips Section */}
        <Card className="mt-6 sm:mt-8 border-amber-200 bg-amber-50">
          <CardHeader className="px-4 sm:px-6 py-4 sm:py-6">
            <CardTitle className="flex items-center gap-2 text-amber-800 text-base sm:text-lg">
              <Zap className="w-4 h-4 sm:w-5 sm:h-5" />
              Pro Tips for Success
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6 pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="flex gap-2 sm:gap-3">
                <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-neutral-900 text-sm sm:text-base">Start with Intake Forms</h4>
                  <p className="text-xs sm:text-sm text-neutral-600">Send intake forms before granting dashboard access to gather essential business info.</p>
                </div>
              </div>
              <div className="flex gap-2 sm:gap-3">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-neutral-900 text-sm sm:text-base">Schedule Content Ahead</h4>
                  <p className="text-xs sm:text-sm text-neutral-600">Use bulletin scheduling and auto-bulletin to maintain consistent engagement.</p>
                </div>
              </div>
              <div className="flex gap-2 sm:gap-3">
                <Target className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-neutral-900 text-sm sm:text-base">Leverage AI Tools</h4>
                  <p className="text-xs sm:text-sm text-neutral-600">Use AI generators to quickly create customized business plans and strategies.</p>
                </div>
              </div>
              <div className="flex gap-2 sm:gap-3">
                <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-neutral-900 text-sm sm:text-base">Regular Feedback</h4>
                  <p className="text-xs sm:text-sm text-neutral-600">Send weekly feedback to Podcast Academy students - they receive email notifications!</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Help Section */}
        <Card className="mt-4 mb-6">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-neutral-900 text-sm sm:text-base">Ready to Get Started?</h3>
                <p className="text-xs sm:text-sm text-neutral-500">Head to your dashboard to begin managing clients</p>
              </div>
              <Button className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto text-sm" onClick={() => navigate("/consultant")}>
                Go to Dashboard
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <CertificatePreview open={showCertificates} onOpenChange={setShowCertificates} />
      <AgentChatDialog open={showAgentChat} onOpenChange={setShowAgentChat} />
    </div>
  );
};

export default ConsultantOnboarding;
